// Delivery Partner Leaderboard — Top 10 earners
import React, { useCallback, useEffect, useState } from 'react';
import { Trophy, Star } from 'lucide-react';
import api from '../utils/api';

const MEDALS = ['🥇', '🥈', '🥉'];

export default function DeliveryLeaderboard({ city = '', compact = false, limit = 10 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/delivery/leaderboard', { params: { limit, city: city || undefined } });
      setRows(r.data.leaderboard || []);
    } catch {} finally { setLoading(false); }
  }, [city, limit]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center text-xs opacity-60 py-4">⏳ Loading leaderboard…</div>;
  if (rows.length === 0) return <div className="text-center text-xs opacity-60 py-4">No active partners yet</div>;

  return (
    <div className="rounded-2xl bg-gradient-to-br from-amber-950/60 to-orange-950/40 border border-amber-400/40 p-3 space-y-1" data-testid="delivery-leaderboard">
      <div className="flex items-center gap-2 mb-2">
        <Trophy size={16} className="text-amber-400" />
        <div className="text-sm font-black text-amber-200">Top Delivery Partners{city ? ` · ${city}` : ''}</div>
      </div>
      {rows.map((p, i) => (
        <div key={p.id} data-testid={`leaderboard-row-${i + 1}`}
             className={`p-2 rounded-lg flex items-center gap-2 ${i < 3 ? 'bg-amber-500/10 border border-amber-400/30' : 'bg-slate-800/40'}`}>
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-black bg-slate-900/60 shrink-0">
            {MEDALS[i] || <span className="text-[10px] opacity-70">#{i + 1}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-black truncate">{p.name}</div>
            <div className="text-[10px] opacity-70 truncate">
              📍 {p.city} · 🛵 {p.vehicle_type} · 📦 {p.total_deliveries} deliveries
              {p.rating_count > 0 && (
                <span className="ml-1 inline-flex items-center gap-0.5 text-yellow-300">
                  · <Star size={9} className="fill-yellow-300 text-yellow-300" /> {p.rating_avg}
                </span>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-black text-amber-300">{Math.round(p.total_earned_at)}</div>
            <div className="text-[9px] opacity-60">AT earned</div>
          </div>
        </div>
      ))}
      {compact || (
        <div className="text-[9px] opacity-50 text-center pt-1">Zero-cost · Refreshes on page load</div>
      )}
    </div>
  );
}
